import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchMatchProposals, fetchMyMatchProposals, createMatchProposal,
    acceptMatchProposal, cancelMatchProposal, checkCourtAvailability,
    fetchAvailableArrangementsForProposal,
    fetchLeagueView, fetchMyActivity, getUserData, redirectToLoginIfInvalidToken,
    freezeLeagueParticipation, unfreezeLeagueParticipation
} from '../../api/api';
import Spinner from '../Spinner/Spinner';
import CustomModal from '../../UI/Modal/CustomModal';
import styles from './MatchPlanning.module.css';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const formatProposalDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const day = format(date, 'dd', { locale: pl });
    const month = format(date, 'LLLL', { locale: pl });
    const dayName = format(date, 'EEEE', { locale: pl });
    return `${day} ${month} (${dayName})`;
};

const extractErrorMessage = (data) => {
    if (!data) return 'Nieznany błąd';
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    return JSON.stringify(data);
};

const MatchPlanning = () => {
    const { tournamentId } = useParams();
    const navigate = useNavigate();

    const [leagueData, setLeagueData] = useState(null);
    const [userData, setUserData] = useState({});
    const [proposals, setProposals] = useState([]);
    const [myProposals, setMyProposals] = useState([]);
    const [myActivity, setMyActivity] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [proposedDate, setProposedDate] = useState('');
    const [proposedTime, setProposedTime] = useState('');
    const [availability, setAvailability] = useState(null);
    const [isChecking, setIsChecking] = useState(false);

    const [modalMessage, setModalMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPositive, setIsPositive] = useState(true);
    const [isWarning, setIsWarning] = useState(false);
    const [confirmCallback, setConfirmCallback] = useState(null);

    const [cancelModal, setCancelModal] = useState(null); // { proposalId, status }
    const [cancelReason, setCancelReason] = useState('');

    const [courtPickModal, setCourtPickModal] = useState(null); // { proposalId, arrangements, message? }
    const [statusFilter, setStatusFilter] = useState({
        ACTIVE: true, ACCEPTED: true, EXPIRED: true, CANCELED: true
    });
    const toggleStatusFilter = (status) => {
        setStatusFilter(prev => ({ ...prev, [status]: !prev[status] }));
    };

    useEffect(() => {
        redirectToLoginIfInvalidToken(navigate, `/leagues/${tournamentId}/match-planning`);
    }, [navigate, tournamentId]);

    useEffect(() => {
        const fetchUser = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchUser();
    }, []);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const leagueResult = await fetchLeagueView(tournamentId, null);
        if (leagueResult.success) {
            setLeagueData(leagueResult.data);
            const round = leagueResult.data.currentRoundNumber;

            const [proposalsResult, myResult, activityResult] = await Promise.all([
                fetchMatchProposals(tournamentId, round),
                fetchMyMatchProposals(tournamentId),
                fetchMyActivity(tournamentId)
            ]);

            if (proposalsResult.success) setProposals(proposalsResult.data);
            if (myResult.success) setMyProposals(myResult.data);
            if (activityResult.success) setMyActivity(activityResult.data);
        }
        setIsLoading(false);
    }, [tournamentId]);

    useEffect(() => { loadData(); }, [loadData]);

    const showModal = (message, positive = true) => {
        setModalMessage(message);
        setIsPositive(positive);
        setIsWarning(false);
        setConfirmCallback(null);
        setIsModalOpen(true);
    };

    const showConfirm = (message, onConfirm, positive = true, warning = false) => {
        setModalMessage(message);
        setIsPositive(positive);
        setIsWarning(warning);
        setConfirmCallback(() => onConfirm);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setConfirmCallback(null);
        setIsWarning(false);
    };

    const handleModalConfirm = () => {
        const cb = confirmCallback;
        closeModal();
        if (cb) cb();
    };

    const handleFreeze = async () => {
        const result = await freezeLeagueParticipation(tournamentId);
        if (result.success) {
            showModal('Zamrożenie zgłoszone. Obowiązuje od następnej rundy.');
            loadData();
        } else {
            showModal(result.data?.message || 'Błąd zamrożenia', false);
        }
    };

    const handleUnfreeze = async () => {
        const result = await unfreezeLeagueParticipation(tournamentId);
        if (result.success) {
            showModal('Odmrożenie zgłoszone.');
            loadData();
        } else {
            showModal(result.data?.message || 'Błąd odmrożenia', false);
        }
    };

    const handleCheckAvailability = async () => {
        if (!proposedDate || !proposedTime) {
            showModal('Wybierz datę i godzinę', false);
            return;
        }
        setIsChecking(true);
        const result = await checkCourtAvailability(tournamentId, proposedDate, proposedTime);
        if (result.success) {
            setAvailability(result.data);
        } else {
            showModal('Błąd sprawdzania dostępności: ' + extractErrorMessage(result.data), false);
        }
        setIsChecking(false);
    };

    const handleCreate = async (confirmed = false) => {
        if (!proposedDate || !proposedTime) {
            showModal('Wybierz datę i godzinę', false);
            return;
        }
        const result = await createMatchProposal(tournamentId, proposedDate, proposedTime, confirmed);
        if (result.success) {
            if (result.data?.requiresConfirmation) {
                showConfirm(
                    'Do planowanego rozegrania meczu zostało mniej niż 3 godziny. Czy na pewno chcesz dodać tę ofertę?',
                    () => handleCreate(true),
                    true,
                    true
                );
                return;
            }
            showModal('Propozycja terminu dodana');
            setProposedDate('');
            setProposedTime('');
            setAvailability(null);
            loadData();
        } else {
            showModal(extractErrorMessage(result.data), false);
        }
    };

    const doAccept = async (proposalId, slots = null, confirmed = false) => {
        const result = await acceptMatchProposal(tournamentId, proposalId, false, slots, confirmed);
        if (result.success) {
            if (result.data?.requiresConfirmation) {
                showConfirm(
                    'Do rozpoczęcia meczu zostało mniej niż 3 godziny. Czy na pewno chcesz przyjąć tą ofertę?',
                    () => doAccept(proposalId, slots, true),
                    true,
                    true
                );
                return;
            }
            const court = result.data?.reservedCourtInfo;
            showModal(`Mecz zaakceptowany! Zarezerwowano ${court || 'kort'}. Sprawdź email.`);
            loadData();
            return;
        }
        // COURT_TAKEN: chosen arrangement is no longer available — re-pick
        if (result.data === 'COURT_TAKEN') {
            await startArrangementSelection(proposalId, 'Wybrany układ jest już zajęty. Wybierz inny.');
            return;
        }
        showModal(extractErrorMessage(result.data), false);
        loadData();
    };

    const startArrangementSelection = async (proposalId, message = null) => {
        const result = await fetchAvailableArrangementsForProposal(tournamentId, proposalId);
        if (!result.success) {
            showModal(extractErrorMessage(result.data), false);
            return;
        }
        const arrangements = result.data || [];
        if (arrangements.length === 0) {
            // Brak dostępnych układów — backend zwróci COURTS_UNAVAILABLE i unieważni propozycję
            await doAccept(proposalId, null);
            return;
        }
        // 1 układ niezeskracany → rezerwuj automatycznie. W każdym innym przypadku
        // (≥2 układy lub układ truncated) pokaż picker, żeby użytkownik świadomie
        // potwierdził wybór (zwłaszcza skrócony 90 min).
        if (arrangements.length === 1 && !arrangements[0].truncated) {
            await doAccept(proposalId, arrangements[0].slots);
            return;
        }
        setCourtPickModal({ proposalId, arrangements: arrangements.slice(0, 3), message });
    };

    const handleAccept = (proposalId) => {
        showConfirm(
            'Czy na pewno chcesz przyjąć tę propozycję? Zostanie utworzona rezerwacja kortu.',
            () => startArrangementSelection(proposalId)
        );
    };

    const handleArrangementPick = async (arrangement) => {
        if (!courtPickModal) return;
        const proposalId = courtPickModal.proposalId;
        setCourtPickModal(null);
        await doAccept(proposalId, arrangement.slots);
    };

    const doCancel = async (proposalId, reason) => {
        const result = await cancelMatchProposal(tournamentId, proposalId, reason || null);
        if (result.success) {
            showModal('Propozycja anulowana');
            loadData();
        } else {
            showModal(extractErrorMessage(result.data), false);
        }
    };

    const handleCancel = (proposalId, status) => {
        if (status === 'ACCEPTED') {
            setCancelReason('');
            setCancelModal({ proposalId });
        } else {
            showConfirm('Czy na pewno chcesz anulować tę propozycję?', () => doCancel(proposalId), false);
        }
    };

    const handleCancelConfirm = () => {
        if (cancelModal) {
            doCancel(cancelModal.proposalId, cancelReason);
            setCancelModal(null);
            setCancelReason('');
        }
    };

    const handleCancelDismiss = () => {
        setCancelModal(null);
        setCancelReason('');
    };

    if (isLoading || !leagueData) return <Spinner />;

    const currentRound = leagueData.currentRoundNumber;
    const userRoles = new Set(userData.authorities || []);
    const isSupervisorOrAdmin = userRoles.has("ROLE_SUPERVISOR") || userRoles.has("ROLE_SUPER") || userRoles.has("ROLE_ADMIN");
    const isFrozen = leagueData.frozenInCurrentRound === true;
    const frozenForNextRound = leagueData.frozenForNextRound === true;
    const canFreeze = !frozenForNextRound && (leagueData.freezesRemaining ?? 0) > 0;
    const userGroupLabel = leagueData.userGroupLabel;

    // Filter groups: regular player sees only their group, admin/supervisor sees all
    const visibleGroups = isSupervisorOrAdmin
        ? leagueData.groupStandings
        : leagueData.groupStandings.filter(g => g.groupLabel === userGroupLabel);

    // Get current user's participantId from league data
    const myParticipantId = leagueData.userParticipantId || null;

    // Build set of played match pairs (participantId pairs where winner is set)
    const allMatches = leagueData.groupStandings.flatMap(g => g.matches);
    const hasPlayedMatch = (pid1, pid2) => {
        return allMatches.some(m =>
            m.participantWinnerId != null &&
            ((m.participant1Id === pid1 && m.participant2Id === pid2) ||
             (m.participant1Id === pid2 && m.participant2Id === pid1))
        );
    };

    // Does current user already have an ACCEPTED proposal with the given opponent in this round?
    // Scans the full proposals list. Ignores past-dated ACCEPTED proposals (stale agreements —
    // mecz nie został rozegrany w zaplanowanym terminie) — takie nie blokują nowej akceptacji.
    const hasAcceptedProposalWith = (opponentParticipantId) => {
        if (!myParticipantId || !opponentParticipantId) return false;
        const now = new Date();
        return proposals.some(p => {
            if (p.status !== 'ACCEPTED') return false;
            const isPair = (p.proposerParticipantId === myParticipantId && p.accepterParticipantId === opponentParticipantId) ||
                           (p.proposerParticipantId === opponentParticipantId && p.accepterParticipantId === myParticipantId);
            if (!isPair) return false;
            const end = new Date(p.proposedDate + 'T' + p.proposedEndTime);
            return end > now;
        });
    };

    // Check if current player has any unplayed matches left
    const hasUnplayedMatches = myParticipantId && allMatches.some(m =>
        m.participantWinnerId == null &&
        (m.participant1Id === myParticipantId || m.participant2Id === myParticipantId)
    );

    // Group proposals by group label — show all statuses up to 6 days after proposed date,
    // dodatkowo filtrowane przez statusFilter (przyciski Aktywna/Zaakceptowana/Anulowana/Wygasła).
    const visibleProposals = proposals
        .filter(p => {
            const proposedDate = new Date(p.proposedDate + 'T00:00:00');
            const cutoff = new Date(proposedDate);
            cutoff.setDate(cutoff.getDate() + 6);
            return new Date() <= cutoff;
        })
        .filter(p => statusFilter[p.status] !== false)
        .sort((a, b) => {
            const aActive = a.status === 'ACTIVE' ? 0 : 1;
            const bActive = b.status === 'ACTIVE' ? 0 : 1;
            if (aActive !== bActive) return aActive - bActive;
            const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
            if (dateCompare !== 0) return dateCompare;
            return a.proposedStartTime.localeCompare(b.proposedStartTime);
        });
    const groupedProposals = {};
    for (const group of visibleGroups) {
        groupedProposals[group.groupLabel] = visibleProposals.filter(p => p.groupLabel === group.groupLabel);
    }

    // Current week boundaries (Monday–Sunday)
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset), 'yyyy-MM-dd');
    const weekEnd = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset + 6), 'yyyy-MM-dd');

    // Count my proposals for status banner — current week only
    const myWeekProposals = myProposals.filter(p =>
        (p.status === 'ACTIVE' || p.status === 'ACCEPTED') &&
        p.proposedDate >= weekStart && p.proposedDate <= weekEnd
    );
    const totalProposed = myWeekProposals.length;
    const afterFiveCount = myWeekProposals.filter(p => {
        const hour = parseInt(p.proposedStartTime.split(':')[0]);
        return hour >= 17;
    }).length;

    // Dane bieżącego tygodnia z breakdownu (zawiera propozycje + rozegrane mecze).
    // Spójne z logiką backendu: rozegrany mecz liczy się jako pełna akcja i spełnia
    // wymóg "po 17:00" — więc banner "Bieżący tydzień" powinien te mecze uwzględniać.
    const currentWeekBreakdown = myActivity?.weeks?.find(w => w.inProgress);
    const currentWeekActions = currentWeekBreakdown?.totalActions ?? totalProposed;
    const currentWeekHasAfter17 = currentWeekBreakdown
        ? (currentWeekBreakdown.hasAfter17 || currentWeekBreakdown.matchesPlayed > 0)
        : afterFiveCount > 0;
    // Łączna suma punktów aktywności w rundzie — z backendu (zawiera tygodnie zamknięte
    // z ostateczną wartością + bieżący tydzień w jego aktualnym stanie, tygodnie
    // nierozpoczęte liczą się jako 0).
    const roundTotalActivity = myActivity?.totalActivityThisRound || 0;

    const timeOptions = [];
    for (let h = 7; h <= 20; h++) {
        timeOptions.push(`${String(h).padStart(2, '0')}:00`);
        if (h < 20) timeOptions.push(`${String(h).padStart(2, '0')}:30`);
    }

    const isAtLeast2hBefore = (proposedDate, proposedStartTime) => {
        const matchStart = new Date(proposedDate + 'T' + proposedStartTime);
        return matchStart.getTime() - Date.now() >= 2 * 60 * 60 * 1000;
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'ACTIVE': return styles.statusActive;
            case 'ACCEPTED': return styles.statusAccepted;
            case 'EXPIRED': return styles.statusExpired;
            case 'CANCELED': return styles.statusCanceled;
            default: return '';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Aktywna';
            case 'ACCEPTED': return 'Zaakceptowana';
            case 'EXPIRED': return 'Wygasła';
            case 'CANCELED': return 'Anulowana';
            default: return status;
        }
    };

    return (
        <div className={styles.container}>
            <button className={styles.backButton} onClick={() => navigate('/tournaments', { state: { activeTab: 'current', tournamentId: parseInt(tournamentId) } })}>
                ← Powrót
            </button>

            <h2 className={styles.title}>Planowanie meczów — Runda {currentRound}</h2>

            {isFrozen ? (
                <div className={styles.frozenBanner}>
                    ❄️ Twój udział w tej rundzie jest zamrożony — nie możesz proponować ani akceptować terminów.
                    {frozenForNextRound && (
                        <button className={styles.unfreezeButton} onClick={handleUnfreeze} style={{ marginLeft: 12 }}>
                            Odmróź udział
                        </button>
                    )}
                </div>
            ) : !hasUnplayedMatches ? (
                <div className={styles.frozenBanner}>
                    Rozegrałeś już wszystkie mecze w tej rundzie lub Twój udział jest zamrożony.
                </div>
            ) : (
                <>
                    <div className={styles.statusBanner}>
                        Bieżący tydzień: <span className={currentWeekActions >= 2 ? styles.statusOk : styles.statusWarning}>
                            {currentWeekActions}/2
                        </span>
                        {' | '}
                        Po 17:00: <span className={currentWeekHasAfter17 ? styles.statusOk : styles.statusWarning}>
                            {currentWeekHasAfter17 ? '✓' : '✗'}
                        </span>
                    </div>

                    {myActivity && myActivity.weeks && myActivity.weeks.length > 0 && (
                        <section className={styles.activitySection}>
                            <button
                                type="button"
                                className={styles.breakdownToggle}
                                onClick={() => setShowBreakdown(!showBreakdown)}
                            >
                                {showBreakdown ? '▼' : '▶'} Moja aktywność w rundzie {myActivity.roundNumber}
                                <span className={styles.activityIndicator}>
                                    {' '}(razem: {roundTotalActivity})
                                </span>
                            </button>
                            {showBreakdown && (
                                <div className={styles.weekBreakdownList}>
                                    {myActivity.weeks.map(week => {
                                        const weekClass = [
                                            styles.weekItem,
                                            week.inProgress ? styles.weekInProgress : '',
                                            week.exempted ? styles.weekExempted : '',
                                            week.notStarted ? styles.weekNotStarted : '',
                                        ].filter(Boolean).join(' ');
                                        return (
                                            <div key={week.weekNumber} className={weekClass}>
                                                <div className={styles.weekHeader}>
                                                    <strong>Tydzień {week.weekNumber}</strong>
                                                    {' '}({format(new Date(week.weekStart + 'T00:00:00'), 'dd.MM')}
                                                    {' – '}
                                                    {format(new Date(week.weekEnd + 'T00:00:00'), 'dd.MM')})
                                                    {week.notStarted && <span className={styles.notStartedBadge}>planowany</span>}
                                                    {week.exempted && <span className={styles.exemptedBadge}>✓ Exempt</span>}
                                                    {week.inProgress && !week.exempted && <span className={styles.inProgressBadge}>w toku</span>}
                                                </div>
                                                {week.notStarted ? (
                                                    <div className={styles.weekDetails}>
                                                        <em>Tydzień jeszcze się nie rozpoczął.</em>
                                                    </div>
                                                ) : (
                                                    <div className={styles.weekDetails}>
                                                        Akcje: <strong>{week.totalActions}/2</strong>
                                                        {' · '}Propozycje: {week.proposalsSubmitted}
                                                        {' · '}Rozegrane mecze: {week.matchesPlayed}
                                                        {' · '}Po 17:00:{' '}
                                                        <strong className={(week.hasAfter17 || week.matchesPlayed > 0) ? styles.statusOk : styles.statusWarning}>
                                                            {(week.hasAfter17 || week.matchesPlayed > 0) ? '✓' : '✗'}
                                                        </strong>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}

                    {/* Propose a new time */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Zaproponuj termin</h3>
                        <div className={styles.formStack}>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Data</label>
                                <input
                                    className={styles.formInput}
                                    type="date"
                                    value={proposedDate}
                                    onChange={(e) => { setProposedDate(e.target.value); setAvailability(null); }}
                                    min={format(new Date(), 'yyyy-MM-dd')}
                                    max={leagueData.roundEndDate ? format(new Date(leagueData.roundEndDate), 'yyyy-MM-dd') : undefined}
                                />
                            </div>
                            <div className={styles.formField}>
                                <label className={styles.formLabel}>Godzina</label>
                                <select className={styles.formInput} value={proposedTime} onChange={(e) => { setProposedTime(e.target.value); setAvailability(null); }}>
                                    <option value="">— wybierz —</option>
                                    {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className={styles.formButtons}>
                                <button className={styles.checkButton} onClick={handleCheckAvailability} disabled={isChecking}>
                                    {isChecking ? 'Sprawdzam...' : 'Sprawdź dostępność'}
                                </button>
                                <button
                                    className={styles.addButton}
                                    onClick={handleCreate}
                                    disabled={!availability || !availability.available}
                                >
                                    Dodaj propozycję
                                </button>
                                {frozenForNextRound ? (
                                    <button className={styles.unfreezeButton} onClick={handleUnfreeze}>
                                        Odmróź udział
                                    </button>
                                ) : canFreeze ? (
                                    <button className={styles.freezeButton} onClick={() => showConfirm(
                                        `Zamrożenie obowiązuje od następnej rundy na 1 rundę. Awanse i spadki zostaną naliczone normalnie. Pozostało zamrożeń: ${leagueData.freezesRemaining}.`,
                                        handleFreeze
                                    )}>
                                        ❄️ Zamróź ({leagueData.freezesRemaining})
                                    </button>
                                ) : null}
                            </div>
                        </div>
                        {availability && (
                            <div className={`${styles.availabilityInfo} ${availability.available ? styles.availabilityOk : styles.availabilityFail}`}>
                                {availability.available
                                    ? 'Termin dostępny'
                                    : (availability.message || 'Brak dostępnych kortów')
                                }
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Group activity leaderboard —
                regular player: tylko własna grupa;
                admin/supervisor: wszystkie widoczne grupy (jedna sekcja per grupa) */}
            {visibleGroups.some(g => g.standings && g.standings.length > 0) && (
                <>
                    {visibleGroups
                        .filter(g => g.standings && g.standings.length > 0)
                        .filter(g => isSupervisorOrAdmin || g.groupLabel === userGroupLabel)
                        .map(group => {
                            // Sortowanie ASC po punktach aktywności — najmniej aktywni na górze
                            // (analogicznie do starej semantyki "najgorsi na górze").
                            const sorted = [...group.standings]
                                .sort((a, b) => (a.activityPointsThisRound || 0) - (b.activityPointsThisRound || 0));
                            return (
                                <section key={group.groupLabel} className={styles.groupPenaltySection}>
                                    <h4>Aktywność w grupie {group.groupLabel}</h4>
                                    <ul className={styles.groupPenaltyList}>
                                        {sorted.map(s => {
                                            const isMe = myParticipantId && s.participantId === myParticipantId;
                                            const activity = s.activityPointsThisRound || 0;
                                            return (
                                                <li
                                                    key={s.participantId}
                                                    className={`${styles.groupPenaltyItem} ${isMe ? styles.groupPenaltyMe : ''}`}
                                                >
                                                    <span className={styles.playerName}>
                                                        {isMe ? <strong>Ty ({s.playerName})</strong> : s.playerName}
                                                    </span>
                                                    <span className={styles.playerPenalty}>
                                                        <span className={styles.activityValue}>{activity}</span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </section>
                            );
                        })}
                </>
            )}

            {/* Active proposals per group */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Propozycje terminów{!isSupervisorOrAdmin && userGroupLabel ? ` — Grupa ${userGroupLabel}` : ''}</h3>
                <div className={styles.statusFilterRow}>
                    {[
                        { key: 'ACTIVE', label: 'Aktywna' },
                        { key: 'ACCEPTED', label: 'Umówiona' },
                        { key: 'CANCELED', label: 'Odwołana' },
                        { key: 'EXPIRED', label: 'Wygasła' },
                    ].map(s => (
                        <button
                            key={s.key}
                            type="button"
                            className={`${styles.statusFilterButton} ${styles[`filter${s.key}`]} ${statusFilter[s.key] ? styles.filterActive : styles.filterInactive}`}
                            onClick={() => toggleStatusFilter(s.key)}
                            aria-pressed={statusFilter[s.key]}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                {visibleGroups.map(group => (
                    <div key={group.groupLabel} className={styles.groupBlock}>
                        {isSupervisorOrAdmin && <div className={styles.groupLabel}>Grupa {group.groupLabel}</div>}
                        {groupedProposals[group.groupLabel]?.length > 0 ? (
                            <>
                                <table className={`${styles.proposalsTable} ${styles.desktopOnly}`}>
                                    <thead>
                                        <tr>
                                            <th>Gracz</th>
                                            <th>Data</th>
                                            <th>Godzina</th>
                                            <th>Status</th>
                                            {!isFrozen && <th>Akcja</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedProposals[group.groupLabel].map(p => {
                                            const isOwnProposal = myParticipantId && p.proposerParticipantId === myParticipantId;
                                            const isAccepter = myParticipantId && p.accepterParticipantId === myParticipantId;
                                            const alreadyPlayed = myParticipantId && hasPlayedMatch(myParticipantId, p.proposerParticipantId);
                                            const alreadyAgreed = hasAcceptedProposalWith(p.proposerParticipantId);
                                            const canAccept = p.status === 'ACTIVE' && !isOwnProposal && !alreadyPlayed && !alreadyAgreed;
                                            const canCancel = p.status === 'ACCEPTED' && (isOwnProposal || isAccepter) && isAtLeast2hBefore(p.proposedDate, p.proposedStartTime);
                                            return (
                                                <tr key={p.id}>
                                                    <td>{p.proposerName}</td>
                                                    <td>{formatProposalDate(p.proposedDate)}</td>
                                                    <td>{p.proposedStartTime.substring(0, 5)}–{p.proposedEndTime.substring(0, 5)}</td>
                                                    <td>
                                                        <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                            {getStatusLabel(p.status)}
                                                        </span>
                                                        {p.status === 'ACCEPTED' && p.accepterName && (
                                                            <span className={styles.accepterInfo}> — {p.accepterName}</span>
                                                        )}
                                                    </td>
                                                    {!isFrozen && (
                                                        <td>
                                                            {canAccept && (
                                                                <button className={styles.acceptButton} onClick={() => handleAccept(p.id)}>
                                                                    Przyjmij
                                                                </button>
                                                            )}
                                                            {canCancel && (
                                                                <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                                    Anuluj
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className={styles.mobileOnly}>
                                    {groupedProposals[group.groupLabel].map(p => {
                                        const isOwnProposal = myParticipantId && p.proposerParticipantId === myParticipantId;
                                        const isAccepter = myParticipantId && p.accepterParticipantId === myParticipantId;
                                        const alreadyPlayed = myParticipantId && hasPlayedMatch(myParticipantId, p.proposerParticipantId);
                                        const alreadyAgreed = hasAcceptedProposalWith(p.proposerParticipantId);
                                        const canAccept = p.status === 'ACTIVE' && !isOwnProposal && !alreadyPlayed && !alreadyAgreed;
                                        const canCancel = p.status === 'ACCEPTED' && (isOwnProposal || isAccepter) && isAtLeast2hBefore(p.proposedDate, p.proposedStartTime);
                                        return (
                                            <div key={p.id} className={styles.proposalCard}>
                                                <div className={styles.proposalCardHeader}>
                                                    <span className={styles.proposalCardPlayer}>{p.proposerName}</span>
                                                    <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                        {getStatusLabel(p.status)}
                                                    </span>
                                                </div>
                                                <div className={styles.proposalCardDetails}>
                                                    {formatProposalDate(p.proposedDate)} · {p.proposedStartTime.substring(0, 5)}–{p.proposedEndTime.substring(0, 5)}
                                                </div>
                                                {p.status === 'ACCEPTED' && p.accepterName && (
                                                    <div className={styles.accepterInfo}>Przyjął: {p.accepterName}</div>
                                                )}
                                                {!isFrozen && canAccept && (
                                                    <button className={styles.acceptButton} onClick={() => handleAccept(p.id)}>
                                                        Przyjmij
                                                    </button>
                                                )}
                                                {!isFrozen && canCancel && (
                                                    <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                        Anuluj
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className={styles.noProposals}>Brak propozycji w ostatnich dniach</div>
                        )}
                    </div>
                ))}
            </div>

            {/* My proposals */}
            {!isFrozen && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Moje propozycje</h3>
                    {myProposals.length > 0 ? (
                        <>
                            <table className={`${styles.proposalsTable} ${styles.desktopOnly}`}>
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Godzina</th>
                                        <th>Status</th>
                                        <th>Akcja</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...myProposals].sort((a, b) => {
                                        const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
                                        if (dateCompare !== 0) return dateCompare;
                                        return a.proposedStartTime.localeCompare(b.proposedStartTime);
                                    }).map(p => (
                                        <tr key={p.id}>
                                            <td>{formatProposalDate(p.proposedDate)}</td>
                                            <td>{p.proposedStartTime.substring(0, 5)}–{p.proposedEndTime.substring(0, 5)}</td>
                                            <td>
                                                <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                    {getStatusLabel(p.status)}
                                                </span>
                                            </td>
                                            <td>
                                                {(p.status === 'ACTIVE' || (p.status === 'ACCEPTED' && isAtLeast2hBefore(p.proposedDate, p.proposedStartTime))) && (
                                                    <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                        Anuluj
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className={styles.mobileOnly}>
                                {[...myProposals].sort((a, b) => {
                                    const dateCompare = a.proposedDate.localeCompare(b.proposedDate);
                                    if (dateCompare !== 0) return dateCompare;
                                    return a.proposedStartTime.localeCompare(b.proposedStartTime);
                                }).map(p => (
                                    <div key={p.id} className={styles.proposalCard}>
                                        <div className={styles.proposalCardHeader}>
                                            <span className={styles.proposalCardDetails}>
                                                {formatProposalDate(p.proposedDate)} · {p.proposedStartTime.substring(0, 5)}–{p.proposedEndTime.substring(0, 5)}
                                            </span>
                                            <span className={`${styles.statusTag} ${getStatusClass(p.status)}`}>
                                                {getStatusLabel(p.status)}
                                            </span>
                                        </div>
                                        {(p.status === 'ACTIVE' || (p.status === 'ACCEPTED' && isAtLeast2hBefore(p.proposedDate, p.proposedStartTime))) && (
                                            <button className={styles.cancelButton} onClick={() => handleCancel(p.id, p.status)}>
                                                Anuluj
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className={styles.noProposals}>Nie masz jeszcze żadnych propozycji</div>
                    )}
                </div>
            )}

            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                message={modalMessage}
                isPositiveMessage={isPositive}
                isWarningMessage={isWarning}
                confirmLabel={confirmCallback ? 'Tak' : undefined}
                onConfirm={confirmCallback ? handleModalConfirm : undefined}
                cancelLabel={confirmCallback ? 'Anuluj' : 'Zamknij'}
                cancelButtonColor={confirmCallback ? undefined : 'green'}
                showConfirmButton={!!confirmCallback}
            />

            {cancelModal && (
                <div className={styles.cancelOverlay} onClick={handleCancelDismiss}>
                    <div className={styles.cancelModalContent} onClick={e => e.stopPropagation()}>
                        <h3>Anulowanie zaakceptowanego meczu</h3>
                        <p className={styles.cancelModalInfo}>Rezerwacja kortu zostanie usunięta, a rywal otrzyma powiadomienie email.</p>
                        <label className={styles.cancelModalLabel}>Powód anulowania:</label>
                        <textarea
                            className={styles.cancelModalTextarea}
                            value={cancelReason}
                            onChange={e => setCancelReason(e.target.value)}
                            rows={3}
                            placeholder="Podaj powód anulowania..."
                        />
                        <div className={styles.cancelModalButtons}>
                            <button className={styles.cancelButton} onClick={handleCancelDismiss}>Nie</button>
                            <button className={styles.addButton} onClick={handleCancelConfirm}>Wykonaj</button>
                        </div>
                    </div>
                </div>
            )}

            {courtPickModal && (
                <div className={styles.cancelOverlay} onClick={() => setCourtPickModal(null)}>
                    <div className={styles.courtPickModalContent} onClick={e => e.stopPropagation()}>
                        <h3>Który układ kortów rezerwujesz?</h3>
                        {courtPickModal.arrangements.every(a => a.truncated) && (
                            <p className={styles.cancelModalInfo}>
                                W tym terminie nie ma wolnych slotów na pełne 2h. Możliwa rezerwacja
                                tylko na 1.5h — wybierz kort lub anuluj.
                            </p>
                        )}
                        {courtPickModal.message && (
                            <p className={styles.cancelModalInfo}>{courtPickModal.message}</p>
                        )}
                        <div className={styles.courtPickButtons}>
                            {courtPickModal.arrangements.map(a => (
                                <button key={a.key} className={styles.courtPickButton}
                                        onClick={() => handleArrangementPick(a)}>
                                    {a.label}
                                </button>
                            ))}
                        </div>
                        <div className={styles.cancelModalButtons}>
                            <button className={styles.cancelButton} onClick={() => setCourtPickModal(null)}>Anuluj</button>
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.infoLinkWrapper}>
                <a href="/liga-info" className={styles.infoLink}>Szczegółowy opis ligi</a>
            </div>
        </div>
    );
};

export default MatchPlanning;
